import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { IconButton, Headline } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-common';
import mapContext from '../../../contexts/map-context';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import { messages } from '../../../i18n/messages';
import { CenterPanel } from './CenterPanel';
import { LegalDisclaimer } from '../../common/LegalDisclaimer/LegalDisclaimer';
import { CustomerReviewList } from './CustomerReviewList';
import { ServiceSelectionBar } from './ServiceSelectionBar';
import { RatingBreakdown } from './RatingBreakdown';
import { FilterSortBar } from './FilterSortBar';
import { UnifiedTheme } from '../../../FeatureApp';
import { attachFocusTrap } from '../../../utils/focusTrap';

enum Animation {
  NONE = '',
  SLIDEIN = 'mobileSlidein',
  SLIDEOUT = 'mobileSlideout',
}

export const MobileFlyout = () => {
  const { ratingSummary, dealerReviews, showComments, oneHeader } = mapContext.useMapState();
  const [animation, setAnimation] = useState(Animation.NONE);
  const dispatch = mapContext.useMapDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const ratingLabel = useI18n(messages.five_star_ratings);

  useEffect(() => {
    // eslint-disable-next-line
    const handleClick = (event: any) => {
      if (
        (containerRef.current && !containerRef.current.contains(event.target)) ||
        closeRef.current?.contains(event.target)
      ) {
        closeModal();
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [containerRef]);

  useEffect(() => {
    setAnimation(Animation.SLIDEIN);
    const previusEl = document.activeElement as HTMLElement | null;
    previouslyFocused.current = previusEl;

    attachFocusTrap(containerRef.current as HTMLDivElement, {
      onEscape: () => {
        dispatch({ type: ActionTypes.setIsFlyoutOpen, payload: false });
        const body = document.querySelector('body');
        if (body) {
          body.style.overflow = 'unset';
        }
      },
    });
  }, []);

  const closeModal = () => {
    setAnimation(Animation.SLIDEOUT);
    setTimeout(() => {
      dispatch({ type: ActionTypes.setIsFlyoutOpen, payload: false });
      dispatch({ type: ActionTypes.setReviewCategoryFilter, payload: 'ALL' });
      dispatch({ type: ActionTypes.setReviewStarFilter, payload: 'All stars' });
      dispatch({ type: ActionTypes.setReviewSort, payload: 'Newest' });
    }, 300);
    const body = document.querySelector('body');
    if (body) {
      body.style.overflow = 'unset';
    }
  };

  const totalRatings = ratingSummary?.totalRatings;

  return (
    <MobileFlyoutContainer
      ref={containerRef}
      animation={animation}
      oneHeader={oneHeader}
      id="mobileFlyout"
    >
      <Header>
        <SubHeader>
          <RatingsText tag="span" variant={{ order: '3', type: 'headline', weight: 'normal' }}>
            {totalRatings} {ratingLabel}
          </RatingsText>
          <div>
            <LegalDisclaimer />
          </div>
        </SubHeader>
      </Header>
      <Div>
        <CenterPanel />
        <RatingBreakdown />
      </Div>

      {showComments && dealerReviews && dealerReviews?.length > 0 && (
        <>
          <ServiceSelectionBar />
          <FilterSortBar />
        </>
      )}
      {showComments && <CustomerReviewList />}
      <CloseButton
        ref={closeRef}
        variant="tertiary"
        icon="cancel"
        size={'xs' as any}
        onClick={closeModal}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && previouslyFocused.current) {
            previouslyFocused.current.focus();
            const body = document.querySelector('body');
            if (body) {
              body.style.overflow = 'unset';
            }
          }
        }}
      />
    </MobileFlyoutContainer>
  );
};
const isLegacy = process.env.IS_LEGACY;

const MobileFlyoutContainer = styled.div.attrs({ 'data-test-id': 'mobile-flyout-component' })<{
  animation: Animation;
  oneHeader: boolean;
}>`
  animation: ${(props) =>
    !props.oneHeader && `${props.animation} cubic-bezier(0.21, 0.55, 0.43, 1) 0.2s forwards`};
  background: ${(props: UnifiedTheme) =>
    !isLegacy ? '#2A313C' : props.theme.sys.color.canvas.default};
  border-radius: 20px;
  bottom: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: auto;
  padding: 28px;
  position: ${(props) => (props.oneHeader ? 'absolute' : 'fixed')};
  width: 100%;
  z-index: 9999;
  scrollbar-color: rgba(0, 0, 0, 0.4) transparent; /* optional for FF */
  scrollbar-width: thin; /* Firefox */

  /* For WebKit browsers (Chrome, Edge, Safari) */
  -webkit-overflow-scrolling: touch;

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  @keyframes mobileSlidein {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes mobileSlideout {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: auto min-content;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled(IconButton)`
  min-width: auto;
  height: auto;
  padding: 0;
  position: absolute;
  top: ${SYS_SPACE_FIXED_100};
  right: ${SYS_SPACE_FIXED_100};
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
`;

const RatingsText = styled(Headline)`
  font-size: 18px;
`;

const Div = styled.div`
  display: inline-flex;
  justify-content: flex-end;
  gap: 46.5px;
`;
